const DUE_DATE_FILTERS = ['today', 'thisWeek', 'thisMonth', 'overdue'] as const;
type DueDateFilter = (typeof DUE_DATE_FILTERS)[number];

export interface IssueListFilters {
  client: string | null;
  priority: string | null;
  requestType: string | null;
  duedate: DueDateFilter | null;
  enseigne: string | null;
  shop: string | null;
  brands: string[];
}

export const DEFAULT_FILTERS: IssueListFilters = {
  client: null,
  priority: null,
  requestType: null,
  duedate: null,
  enseigne: null,
  shop: null,
  brands: [],
};

type AssetValue = { value?: string } | string;
type BrandField = AssetValue | AssetValue[];

interface BrandedIssue {
  mappedFields?: { brand?: BrandField };
}

const valueOf = (item: AssetValue): string => (typeof item === 'string' ? item : item?.value ?? '');

export const brandValuesOf = (issue: BrandedIssue): string[] => {
  const brand = issue.mappedFields?.brand;
  if (!brand) return [];
  const items = Array.isArray(brand) ? brand : [brand];
  return items.map(valueOf).filter((value) => value !== '');
};

export const availableBrands = (issues: BrandedIssue[], selected: string[]): string[] => {
  const brands = new Set(selected);
  issues.forEach((issue) => brandValuesOf(issue).forEach((brand) => brands.add(brand)));
  return Array.from(brands).sort((a, b) => a.localeCompare(b));
};

export const matchesBrands = (issue: BrandedIssue, brands: string[]): boolean =>
  !brands.length || brandValuesOf(issue).some((brand) => brands.includes(brand));

export const withSelected = (options: string[], selected: string | null): string[] =>
  selected === null || options.includes(selected) ? options : [...options, selected];

export type FilterChip = { field: keyof IssueListFilters; label: string; value: string };

const dueDateLabel = (t: (key: string) => string, value: DueDateFilter): string =>
  ({ today: t('Today'), thisWeek: t('This week'), thisMonth: t('This month'), overdue: t('Overdue') })[value];

export const filterChips = (filters: IssueListFilters, t: (key: string) => string): FilterChip[] => {
  const chips: FilterChip[] = [];
  const add = (field: keyof IssueListFilters, label: string, value: string | null) => {
    if (value) chips.push({ field, label, value });
  };

  add('client', t('Client'), filters.client);
  add('brands', t('Brand'), filters.brands.length > 0 ? filters.brands.join(', ') : null);
  add('priority', t('Priority'), filters.priority);
  add('requestType', t('Type'), filters.requestType);
  add('enseigne', t('Banner'), filters.enseigne);
  add('shop', t('Store'), filters.shop);
  add('duedate', t('Due date'), filters.duedate ? dueDateLabel(t, filters.duedate) : null);

  return chips;
};

const storageKey = (accountId: string): string => `issuesList.filters.${accountId}`;

type Json = string | number | boolean | null | Json[] | { [key: string]: Json };

const stringOrNull = (value?: Json): string | null => (typeof value === 'string' && value !== '' ? value : null);

const dueDateOrNull = (value?: Json): DueDateFilter | null => DUE_DATE_FILTERS.find((item) => item === value) ?? null;

const stringArrayOf = (value?: Json): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

export const readFilters = (accountId?: string): IssueListFilters => {
  if (!accountId) return DEFAULT_FILTERS;

  try {
    const raw = localStorage.getItem(storageKey(accountId));
    if (!raw) return DEFAULT_FILTERS;

    const parsed: Json = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return DEFAULT_FILTERS;

    return {
      client: stringOrNull(parsed.client),
      priority: stringOrNull(parsed.priority),
      requestType: stringOrNull(parsed.requestType),
      duedate: dueDateOrNull(parsed.duedate),
      enseigne: stringOrNull(parsed.enseigne),
      shop: stringOrNull(parsed.shop),
      brands: stringArrayOf(parsed.brands),
    };
  } catch {
    return DEFAULT_FILTERS;
  }
};

export const saveFilters = (accountId: string | undefined, filters: IssueListFilters): void => {
  if (!accountId) return;
  try {
    localStorage.setItem(storageKey(accountId), JSON.stringify(filters));
  } catch {
    // ponytail: storage blocked (quota, private mode) — filters just don't survive a reload
  }
};
